import { useEffect, useState } from 'react';
import { useApp } from '../lib/app-context';
import { Icon } from './Icon';

// An installed app can stay open for days; after a deploy it keeps running the old code. When the app comes
// back to the foreground (and every 15 minutes) the current page is compared with the server's, and a quiet
// "new version" bar offers a reload — never automatic, so nothing being typed is lost.

const SCRIPT = /\/assets\/index-[\w-]+\.js/;

function runningBuild(): string | null {
  for (const s of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const m = SCRIPT.exec(s.getAttribute('src') ?? '');
    if (m) return m[0];
  }
  return null; // development server: nothing to compare
}

export function UpdateNotice() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const current = runningBuild();
    if (!current) return;
    let busy = false;
    const check = async () => {
      if (busy || document.visibilityState !== 'visible' || !navigator.onLine) return;
      busy = true;
      try {
        const html = await (await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } })).text();
        const latest = SCRIPT.exec(html)?.[0];
        if (latest && latest !== current) setReady(true);
      } catch {
        /* offline or server busy: try again later */
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(() => void check(), 15 * 60_000);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    void check();
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, []);

  if (!ready) return null;
  return (
    <div className="update-notice" role="status">
      <Icon name="download" size={18} />
      <span>{t('update.ready')}</span>
      <button type="button" className="btn btn-sm btn-primary" onClick={() => location.reload()}>
        {t('update.reload')}
      </button>
    </div>
  );
}
